import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import type { QuoteReview } from '@/components/assistant/quoteReviewContract';
import { api } from '@/lib/api';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { useAcceptQuote } from '@/hooks/useBookings';
import { useAuthStore } from '@/stores/authStore';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { AcceptQuoteScheduleDialog } from './AcceptQuoteScheduleDialog';
import { acceptQuoteErrorMessage } from './acceptQuoteError';

interface Version {
  id: string;
  sequenceNo: number;
  kind: string;
  createdAt: string;
  totalCents: number;
  validUntil: string | null;
  selected: boolean;
  sentAt: string | null;
  recipient: string | null;
  response: string | null;
  delivery: string;
  canSelect: boolean;
}
interface History {
  data: Version[];
  legacySends: { id: string; sentAt: string; recipient: string }[];
}
interface Detail {
  sends?: {
    sentAt: string;
    recipient: string;
    channel: string;
    confirmed: boolean;
    response: string | null;
  }[];
  id: string;
  sequenceNo: number;
  kind: string;
  review: QuoteReview;
}

/** A preview only: the live work order changes only after the schedule is confirmed. */
function quoteSchedulePreview(current: WorkOrderDetail, review: QuoteReview): WorkOrderDetail {
  return {
    ...current,
    estimatedDurationHours: review.schedule.estimatedDurationHours,
    customerNotes: review.customerNotes,
    customerWaits: review.schedule.customerWaits,
    allowMinorMaintenance: review.schedule.allowMinorMaintenance,
    jointInspectionRequested: review.schedule.jointInspectionRequested,
    preapprovedAdditionalWorkLimitCents: review.schedule.preapprovedAdditionalWorkLimitCents,
    totalWithVatCents: review.pricing.grossTotalCents,
    services: review.services.map((s) => ({
      id: s.serviceId,
      bookingServiceId: s.bookingServiceId,
      name: s.name,
      estimatedDurationHours: s.plannedHours,
      plannedHoursManual: s.plannedHoursManual,
      assignedMechanicId:
        current.services.find((c) => c.bookingServiceId === s.bookingServiceId)
          ?.assignedMechanicId ?? current.assignedMechanicId,
      status: 'pending',
      startedAt: null,
      pausedAt: null,
      completedAt: null,
      notes: s.notes,
      mechanic: null,
      laborItems: [],
      parts: [],
      extras: [],
    })),
  };
}

export function QuoteHistory({ workOrder }: { workOrder: WorkOrderDetail }) {
  const { t, i18n } = useTranslation('workorders');
  const canUpdate = useAuthStore((s) => s.hasPermission('work_orders.update'));
  const [opened, setOpened] = useState<string | null>(null);
  const [selected, setSelected] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const accept = useAcceptQuote();
  const history = useQuery({
    queryKey: [
      'workOrders',
      'detail',
      workOrder.id,
      'quoteVersions',
      workOrder.updatedAt,
      workOrder.quoteSend?.id,
    ],
    queryFn: () => api.get<History>(`/work-orders/${workOrder.id}/quote-versions`),
  });
  const detail = useQuery({
    queryKey: ['workOrders', 'detail', workOrder.id, 'quoteVersion', opened],
    queryFn: () => api.get<Detail>(`/work-orders/${workOrder.id}/quote-versions/${opened}`),
    enabled: !!opened,
  });
  const money = (cents: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(
      cents / 100
    );
  const date = (value: string) => new Date(value).toLocaleString(i18n.language);
  const showPdf = async () => {
    try {
      const blob = await api.getBlob(
        `/work-orders/${workOrder.id}/quote-versions/${opened}?pdf=true`
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tarjous-${detail.data?.sequenceNo ?? ''}.pdf`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError(t('quoteHistory.loadFailed'));
    }
  };
  const chosen = history.data?.data.find((v) => v.id === opened);
  if (history.isPending) return <CircularProgress size={20} />;
  if (history.isError) return <Alert severity="error">{t('quoteHistory.loadFailed')}</Alert>;
  if (!history.data || (!history.data.data.length && !history.data.legacySends.length)) return null;
  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="h6">{t('quoteHistory.title')}</Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {history.data.data.map((v) => (
          <Box key={v.id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Typography>
              {t(v.kind === 'draft_backup' ? 'quoteHistory.backup' : 'quoteHistory.version', {
                number: v.sequenceNo,
              })}{' '}
              · {money(v.totalCents)} · {date(v.sentAt ?? v.createdAt)}
              {v.recipient ? ` · ${v.recipient}` : ''}
            </Typography>
            {v.selected && <Chip label={t('quoteHistory.selected')} size="small" color="success" />}
            {v.kind === 'sent' && v.delivery !== 'provider_accepted' && (
              <Chip
                label={t(
                  v.delivery === 'not_sent' ? 'quoteHistory.notSent' : 'quoteHistory.unconfirmed'
                )}
                size="small"
                color="warning"
              />
            )}
            {v.response && (
              <Chip
                label={t(
                  v.response === 'accepted' ? 'quoteHistory.accepted' : 'quoteHistory.rejected'
                )}
                size="small"
              />
            )}
            <Button
              onClick={() => {
                setError(null);
                setOpened(v.id);
              }}
            >
              {t('quoteHistory.view')}
            </Button>
          </Box>
        ))}
        {history.data.legacySends.map((s) => (
          <Alert key={s.id} severity="info">
            {date(s.sentAt)} · {s.recipient}: {t('quoteHistory.legacy')}
          </Alert>
        ))}
      </Stack>
      <ResponsiveDialog open={!!opened} onClose={() => setOpened(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('quoteHistory.title')}</DialogTitle>
        <DialogContent>
          {(error || detail.isError) && (
            <Alert severity="error">{error ?? t('quoteHistory.loadFailed')}</Alert>
          )}
          {detail.isPending ? (
            <CircularProgress />
          ) : (
            detail.data && (
              <Stack spacing={2}>
                <Typography variant="h6">
                  {t('quoteHistory.version', { number: detail.data.sequenceNo })} ·{' '}
                  {money(detail.data.review.pricing.grossTotalCents)}
                </Typography>
                {detail.data.review.quoteExpiresAt && (
                  <Typography>
                    {t('quoteHistory.validUntil', {
                      date: date(detail.data.review.quoteExpiresAt),
                    })}
                  </Typography>
                )}
                {detail.data.review.pricing.lines.map((line, index) => (
                  <Typography key={index}>
                    {line.description} · {line.quantity} · {money(line.lineTotalCents)}{' '}
                    {t('quoteHistory.net')}
                  </Typography>
                ))}
                {detail.data.review.customerNotes && (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {detail.data.review.customerNotes}
                  </Typography>
                )}
                {detail.data.sends?.map((send, index) => (
                  <Typography key={index} variant="body2">
                    {date(send.sentAt)} · {send.recipient} · {send.channel} ·{' '}
                    {t(send.confirmed ? 'quoteHistory.sent' : 'quoteHistory.unconfirmed')}
                  </Typography>
                ))}
                {detail.data.kind === 'sent' && (
                  <Button onClick={() => void showPdf()}>{t('quoteHistory.pdf')}</Button>
                )}
                {workOrder.status === 'quote' && canUpdate && detail.data.kind === 'sent' && (
                  <>
                    {!chosen?.canSelect && (
                      <Alert severity="info">{t('quoteHistory.notSelectable')}</Alert>
                    )}
                    <Button
                      variant="contained"
                      disabled={!chosen?.canSelect}
                      onClick={() => {
                        setSelected(detail.data!);
                        setOpened(null);
                      }}
                    >
                      {t('quoteHistory.select')}
                    </Button>
                  </>
                )}
              </Stack>
            )
          )}
        </DialogContent>
      </ResponsiveDialog>
      {selected && (
        <AcceptQuoteScheduleDialog
          key={selected.id}
          open
          workOrder={quoteSchedulePreview(workOrder, selected.review)}
          onCancel={() => {
            setSelected(null);
            setError(null);
          }}
          loading={accept.isPending}
          quoteVersionLabel={`${t('quoteHistory.version', { number: selected.sequenceNo })} · ${money(selected.review.pricing.grossTotalCents)}`}
          errorMessage={error}
          onConfirm={async (data, expectedUpdatedAt) => {
            try {
              await accept.mutateAsync({
                id: workOrder.id,
                expectedUpdatedAt,
                data: {
                  ...data,
                  quoteVersionId: selected.id,
                  customerWaits: data.customerWaits ?? false,
                  allowMinorMaintenance: data.allowMinorMaintenance ?? false,
                  jointInspectionRequested: data.jointInspectionRequested ?? false,
                  preapprovedAdditionalWorkLimitCents:
                    data.preapprovedAdditionalWorkLimitCents ?? null,
                },
              });
              setSelected(null);
              setError(null);
              void history.refetch();
            } catch (cause) {
              setError(acceptQuoteErrorMessage(cause, t));
              throw cause;
            }
          }}
        />
      )}
    </Box>
  );
}
